import React from 'react';
import { FadeIn } from './ui/FadeIn';
import { Pin, Star } from 'lucide-react';
export function ProductPresentation() {
  const chapters = [{
    number: 1,
    title: 'La anatomía del caos',
    bullets: ['Por qué no te escucha', 'Por qué no puede parar aunque quiera', 'Cómo distinguir entre crisis real y protesta estratégica']
  }, {
    number: 2,
    title: 'El error de los primeros 30 segundos',
    bullets: ['Frases que APAGAN el incendio 🔵', 'Frases que lo ALIMENTAN sin darte cuenta 🔴']
  }, {
    number: 3,
    title: 'El Método Espejo Rápido — paso a paso',
    isImportant: true,
    steps: ['PASO 1 → Etiqueta la tormenta (0–60 seg)', 'PASO 2 → Ancla con la voz (60–120 seg)', 'PASO 3 → Cierra con presencia (120–180 seg)'],
    bullets: ['Scripts listos para usar', 'Checklists de aplicación', 'Señales de que está funcionando']
  }, {
    number: 4,
    title: 'Escenarios de alto riesgo',
    bullets: ['🛒 El supermercado', '🌙 La hora de dormir', '🚿 El baño', '🚗 Las salidas']
  }, {
    number: 5,
    title: 'Tu blindaje diario',
    bullets: ['Cómo hablar después de la tormenta', 'Cómo detectar señales antes de que explote', 'Cómo prevenir el 80% de los berrinches de la próxima semana']
  }, {
    number: 6,
    title: 'FAQ: Las preguntas de las 11 PM',
    bullets: ['❓ ¿Qué hago si me pega?', '❓ ¿Qué pasa si exploto yo también?', '❓ ¿Y si no funciona en exactamente 3 minutos?']
  }];
  return <section className="py-24 px-6 bg-white" data-id="element-257">
      <div className="max-w-5xl mx-auto" data-id="element-258">
        <FadeIn data-id="element-259">
          <div className="text-center mb-16" data-id="element-260">
            <h2 className="text-4xl md:text-5xl font-bold text-brand-charcoal mb-6" data-id="element-261">
              PROTOCOLO CALMA 3 MINUTOS
            </h2>
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto font-serif italic" data-id="element-262">
              La guía táctica para detener berrinches y crisis emocionales sin
              gritar, sin amenazas y sin perder los nervios
            </p>
          </div>
        </FadeIn>

        {/* NO vs YES Table */}
        <FadeIn delay={0.1} data-id="element-263">
          <div className="grid md:grid-cols-2 gap-6 mb-20 max-w-3xl mx-auto" data-id="element-264">
            <div className="bg-gray-50 p-6 rounded-2xl border-2 border-gray-200" data-id="element-265">
              <h3 className="text-xl font-bold text-red-600 mb-4 flex items-center gap-2" data-id="element-266">
                <span data-id="element-267">❌</span> NO ES
              </h3>
              <ul className="space-y-3 text-gray-700" data-id="element-268">
                <li data-id="element-269">Un libro de teoría de crianza</li>
                <li data-id="element-270">300 páginas de conceptos abstractos</li>
                <li data-id="element-271">Algo para leer algún día</li>
              </ul>
            </div>
            <div className="bg-brand-sage-light p-6 rounded-2xl border-2 border-brand-sage" data-id="element-272">
              <h3 className="text-xl font-bold text-brand-sage mb-4 flex items-center gap-2" data-id="element-273">
                <span data-id="element-274">✅</span> SÍ ES
              </h3>
              <ul className="space-y-3 text-gray-800 font-medium" data-id="element-275">
                <li data-id="element-276">Una guía táctica de intervención</li>
                <li data-id="element-277">90 minutos de lectura y aplicación directa</li>
                <li data-id="element-278">Algo para usar esta misma noche</li>
              </ul>
            </div>
          </div>
        </FadeIn>

        {/* Chapters */}
        <FadeIn data-id="element-279">
          <h3 className="text-3xl font-bold text-center mb-12" data-id="element-280">
            Esto es lo que viene adentro
          </h3>
        </FadeIn>

        <div className="space-y-6" data-id="element-281">
          {chapters.map((chapter, index) => <FadeIn key={index} delay={index * 0.1} data-id="element-282">
              <div className={`bg-white p-8 rounded-2xl shadow-sm border-2 ${chapter.isImportant ? 'border-brand-orange bg-orange-50/30' : 'border-gray-100'}`} data-id="element-283">
                <div className="flex items-start gap-4" data-id="element-284">
                  <Pin className="w-6 h-6 text-brand-orange flex-shrink-0 mt-1" data-id="element-285" />
                  <div className="flex-1" data-id="element-286">
                    <div className="flex items-center gap-3 mb-4" data-id="element-287">
                      <h4 className="text-xl font-bold text-gray-900" data-id="element-288">
                        CAPÍTULO {chapter.number}
                      </h4>
                      {chapter.isImportant && <span className="flex items-center gap-1 bg-brand-orange text-white px-3 py-1 rounded-full text-sm font-bold" data-id="element-289">
                          <Star className="w-4 h-4 fill-current" data-id="element-290" />
                          EL MÁS IMPORTANTE
                        </span>}
                    </div>
                    <p className="text-xl font-bold text-brand-charcoal mb-4" data-id="element-291">
                      {chapter.title}
                    </p>

                    {chapter.steps && <div className="bg-white p-4 rounded-xl mb-4 border border-brand-orange/30" data-id="element-292">
                        {chapter.steps.map((step, i) => <p key={i} className="text-gray-700 font-medium mb-2 last:mb-0" data-id="element-293">
                            {step}
                          </p>)}
                      </div>}

                    <ul className="space-y-2" data-id="element-294">
                      {chapter.bullets.map((bullet, i) => <li key={i} className="flex items-start gap-2 text-gray-600" data-id="element-295">
                          <span className="mt-1" data-id="element-296">▸</span>
                          <span data-id="element-297">{bullet}</span>
                        </li>)}
                    </ul>
                  </div>
                </div>
              </div>
            </FadeIn>)}
        </div>
      </div>
    </section>;
}